import Image from "next/image";
import { z } from "zod";

import { RichText } from "@/components/rich-text";
import { cn } from "@/lib/utils";
import { customCardSchema } from "@/schemas";

import { AnimationPlayer } from "../animation-player";
import { VideoPlayer } from "./shared/video-player";

export function WithTimer({
  className,
  ...props
}: z.infer<typeof customCardSchema> & { className?: string }) {
  const {
    title,
    richTextTitle,
    description,
    image,
    video,
    mediaType,
    bgColor,
    time,
    videoAnimation,
    isLightText,
    animation,
    loopAnimation,
  } = props;

  const backgroundColor = bgColor?.value ?? "#FAF9F8";

  return (
    <article
      className={cn(
        "group rounded-container mx-auto flex h-full max-w-[530px] flex-col gap-4 overflow-hidden lg:gap-5",
        className
      )}
    >
      <div
        className="rounded-container relative flex w-full overflow-hidden"
        style={{ backgroundColor }}
      >
        {image?.src && mediaType === "image" && (
          <Image
            className="size-full overflow-hidden object-cover"
            src={image.src}
            alt={image.alt ?? "Large Card Media"}
            width={image.width ?? 0}
            height={image.height ?? 0}
            sizes="(min-width: 624px) 50vw, 100vw"
          />
        )}
        {video && mediaType === "video" && video?.asset && (
          <div className="flex size-full flex-1 items-center justify-center">
            <VideoPlayer
              className="size-full object-cover"
              asset={video.asset}
              animation={false}
            />
          </div>
        )}
        {mediaType === "videoAnimation" && videoAnimation?.asset && (
          <div className="flex h-fit items-center justify-center">
            <VideoPlayer
              className="size-full object-contain"
              asset={videoAnimation.asset}
              animation={true}
            />
          </div>
        )}
        {animation && mediaType === "animation" && (
          <AnimationPlayer animation={animation} loop={loopAnimation} />
        )}
      </div>
      <div className="flex flex-col justify-between gap-6">
        <div className="flex flex-col gap-1 lg:gap-3">
          {(richTextTitle || title) && (
            <h3
              className={cn("text-heading-16 lg:text-heading-20 text-black", {
                "text-white": isLightText,
              })}
            >
              {richTextTitle ? <RichText value={richTextTitle} /> : title}
            </h3>
          )}
          <div className="flex flex-col gap-1">
            <span className="text-body lg:text-body-md line-clamp-4 max-w-[395px] text-black lg:line-clamp-3">
              {description}
            </span>
          </div>
        </div>

        {time && (
          <div className="flex items-center gap-2">
            <img src="/icons/clock.svg" alt="Clock Icon" className="size-5" />
            <span className="text-label-10 text-black">
              {time.toLocaleUpperCase()}
            </span>
          </div>
        )}
      </div>
    </article>
  );
}
